/**
 * AuthContext — 관리자 인증 상태 관리
 *
 * 설계:
 * - Firebase Auth onAuthStateChanged 리스너로 세션 복구
 * - ID 토큰은 Firebase SDK가 자동 갱신 (1시간마다)
 * - api/client.ts가 이 컨텍스트의 getIdToken()을 호출해서 Authorization 헤더 주입
 * - 로그인 후 isAdmin 검증은 백엔드 API 호출 시점에 자동 수행
 *   (admins 컬렉션 미등록자는 403 응답)
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  /** Firebase Auth ID 토큰 — API 호출 시 Authorization 헤더용 */
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Firebase Auth 상태 구독
  useEffect(() => {
    let unsub: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      try {
        const { getAuthInstance } = await import('@/config/firebase');
        const { onAuthStateChanged } = await import('firebase/auth');
        const auth = await getAuthInstance();

        if (cancelled) return;

        unsub = onAuthStateChanged(auth, (fbUser) => {
          if (fbUser) {
            setUser({
              uid: fbUser.uid,
              email: fbUser.email,
              displayName: fbUser.displayName,
            });
          } else {
            setUser(null);
          }
          setLoading(false);
        });
      } catch (err) {
        console.error('[Auth] Firebase 초기화 실패:', err);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      unsub?.();
    };
  }, []);

  const getIdToken = useCallback(
    async (forceRefresh = false): Promise<string | null> => {
      try {
        const { getAuthInstance } = await import('@/config/firebase');
        const auth = await getAuthInstance();
        const current = auth.currentUser;
        if (!current) return null;
        return await current.getIdToken(forceRefresh);
      } catch (err) {
        console.error('[Auth] ID 토큰 획득 실패:', err);
        return null;
      }
    },
    []
  );

  const signIn = useCallback(async (email: string, password: string) => {
    const { getAuthInstance } = await import('@/config/firebase');
    const { signInWithEmailAndPassword } = await import('firebase/auth');
    const auth = await getAuthInstance();
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signOut = useCallback(async () => {
    const { getAuthInstance } = await import('@/config/firebase');
    const { signOut: fbSignOut } = await import('firebase/auth');
    const auth = await getAuthInstance();
    await fbSignOut(auth);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, getIdToken, signIn, signOut }),
    [user, loading, getIdToken, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth는 <AuthProvider> 내부에서만 사용할 수 있습니다.');
  }
  return ctx;
}

// ================================================================
// ID 토큰 획득 함수를 API 클라이언트에 주입하기 위한 전역 참조
// (Context는 훅에서만 접근 가능하므로, API 클라이언트가 사용할 수 있도록
//  AuthProvider 마운트 시 전역에 등록)
// ================================================================

let _tokenGetter: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

export function registerTokenGetter(
  fn: (forceRefresh?: boolean) => Promise<string | null>
): void {
  _tokenGetter = fn;
}

export async function getCurrentIdToken(forceRefresh = false): Promise<string | null> {
  if (!_tokenGetter) return null;
  return _tokenGetter(forceRefresh);
}
